
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

declare global {
  interface Window {
    changeLanguage: (lang: string) => void;
  }
}

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [isLegalOpen, setIsLegalOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('EN');
  // Mobile accordion states - closed by default
  const [isMobileCompanyOpen, setIsMobileCompanyOpen] = useState(false);
  const [isMobileLegalOpen, setIsMobileLegalOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const languages = [
    { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'FR', name: 'Français', flag: '🇫🇷' },
    { code: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
    { code: 'de', label: 'DE', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ar', label: 'AR', name: 'العربية', flag: '🇸🇦' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for auto-detected language from Google Translate
  useEffect(() => {
    const handleLanguageDetected = (event: Event) => {
      const customEvent = event as CustomEvent;
      const langCode = customEvent.detail;
      const lang = languages.find(l => l.code === langCode);
      if (lang) {
        setCurrentLang(lang.label);
      }
    };
    window.addEventListener('languageDetected', handleLanguageDetected);
    return () => window.removeEventListener('languageDetected', handleLanguageDetected);
  }, []);

  // Reset mobile accordion states when menu closes
  useEffect(() => {
    if (!isMobileMenuOpen) {
      setIsMobileCompanyOpen(false);
      setIsMobileLegalOpen(false);
    }
  }, [isMobileMenuOpen]);

  const handleLanguageChange = (lang: string, label: string) => {
    setCurrentLang(label);
    setIsLangOpen(false);
    if (window.changeLanguage) {
      window.changeLanguage(lang);
    }
  };

  const services = [
    { name: 'Ocean Freight', icon: 'ri-ship-line', path: '/services#ocean' },
    { name: 'Air Freight', icon: 'ri-plane-line', path: '/services#air' },
    { name: 'Road Transport', icon: 'ri-truck-line', path: '/services#road' },
    { name: 'Rail Logistics', icon: 'ri-train-line', path: '/services#rail' },
  ];

  const companyLinks = [
    { name: 'About', icon: 'ri-information-line', path: '/about' },
    { name: 'Careers', icon: 'ri-briefcase-line', path: '/careers' },
    { name: 'Press', icon: 'ri-newspaper-line', path: '/press' },
    { name: 'Become a Partner', icon: 'ri-handshake-line', path: '/partner' },
  ];

  const legalLinks = [
    { name: 'FAQ', icon: 'ri-question-line', path: '/faq' },
    { name: 'Legal Notices', icon: 'ri-file-text-line', path: '/legal-notices' },
    { name: 'Privacy Policy', icon: 'ri-shield-check-line', path: '/privacy-policy' },
    { name: 'Terms & Conditions', icon: 'ri-file-list-line', path: '/terms' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://static.readdy.ai/image/23008dfd20f0ddb4783f4060c01d33e6/b057788adbbe6a3240b4a9ae454c8b18.png"
              alt="TrackVerse Logo"
              className="h-12 w-auto"
            />
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-primary-blue' : 'text-white hover:text-primary-green'
              } ${location.pathname === '/' ? (isScrolled ? 'text-primary-blue' : 'text-primary-green') : ''}`}
            >
              Home
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <Link
                to="/services"
                className={`font-medium transition-colors flex items-center space-x-1 whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-primary-blue' : 'text-white hover:text-primary-green'
                } ${location.pathname === '/services' ? (isScrolled ? 'text-primary-blue' : 'text-primary-green') : ''}`}
              >
                <span>Services</span>
                <i className="ri-arrow-down-s-line"></i>
              </Link>

              <AnimatePresence>
                {isServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-4 px-2"
                  >
                    {services.map((service) => (
                      <a
                        key={service.name}
                        href={service.path}
                        className="flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-blue rounded-lg">
                          <i className={`${service.icon} text-white text-xl`}></i>
                        </div>
                        <span className="font-medium text-gray-700">{service.name}</span>
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div
              className="relative"
              onMouseEnter={() => setIsCompanyOpen(true)}
              onMouseLeave={() => setIsCompanyOpen(false)}
            >
              <button
                className={`font-medium transition-colors flex items-center space-x-1 whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-primary-blue' : 'text-white hover:text-primary-green'
                } ${['/about', '/careers', '/press', '/partner'].includes(location.pathname) ? (isScrolled ? 'text-primary-blue' : 'text-primary-green') : ''}`}
              >
                <span>Company</span>
                <i className="ri-arrow-down-s-line"></i>
              </button>

              <AnimatePresence>
                {isCompanyOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-4 px-2"
                  >
                    {companyLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        className="flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-green rounded-lg">
                          <i className={`${link.icon} text-white text-xl`}></i>
                        </div>
                        <span className="font-medium text-gray-700">{link.name}</span>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div
              className="relative"
              onMouseEnter={() => setIsLegalOpen(true)}
              onMouseLeave={() => setIsLegalOpen(false)}
            >
              <button
                className={`font-medium transition-colors flex items-center space-x-1 whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-primary-blue' : 'text-white hover:text-primary-green'
                } ${['/faq', '/legal-notices', '/privacy-policy', '/terms'].includes(location.pathname) ? (isScrolled ? 'text-primary-blue' : 'text-primary-green') : ''}`}
              >
                <span>Legal & Support</span>
                <i className="ri-arrow-down-s-line"></i>
              </button>

              <AnimatePresence>
                {isLegalOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-4 px-2"
                  >
                    {legalLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        className="flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-blue rounded-lg">
                          <i className={`${link.icon} text-white text-xl`}></i>
                        </div>
                        <span className="font-medium text-gray-700">{link.name}</span>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/tracking"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-primary-blue' : 'text-white hover:text-primary-green'
              } ${location.pathname === '/tracking' ? (isScrolled ? 'text-primary-blue' : 'text-primary-green') : ''}`}
            >
              Tracking
            </Link>

            <Link
              to="/contact"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-primary-blue' : 'text-white hover:text-primary-green'
              } ${location.pathname === '/contact' ? (isScrolled ? 'text-primary-blue' : 'text-primary-green') : ''}`}
            >
              Contact
            </Link>

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center space-x-1.5 font-medium transition-colors cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-primary-blue' : 'text-white hover:text-primary-green'
                }`}
              >
                <i className="ri-global-line text-lg"></i>
                <span>{currentLang}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>

              <AnimatePresence>
                {isLangOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-44 bg-white rounded-lg shadow-xl py-2 overflow-hidden"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code, lang.label)}
                        className={`w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer ${
                          currentLang === lang.label ? 'bg-gray-50 text-primary-blue' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="font-medium">{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/tracking"
              className="px-6 py-2.5 bg-primary-green text-white font-semibold rounded-lg hover:bg-primary-green/90 transition-all whitespace-nowrap cursor-pointer"
            >
              Track Package
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t"
          >
            <div className="px-6 py-4 space-y-2">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-gray-700 hover:text-primary-blue font-medium whitespace-nowrap"
              >
                Home
              </Link>
              <Link
                to="/services"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-gray-700 hover:text-primary-blue font-medium whitespace-nowrap"
              >
                Services
              </Link>
              
              {/* Mobile Company Menu - Accordion */}
              <div className="border-t border-gray-100">
                <button
                  onClick={() => setIsMobileCompanyOpen(!isMobileCompanyOpen)}
                  className="w-full flex items-center justify-between py-3 text-gray-700 font-medium cursor-pointer"
                >
                  <span>Company</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileCompanyOpen ? 'rotate-180' : ''}`}></i>
                </button>
                <AnimatePresence>
                  {isMobileCompanyOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pb-2 space-y-1">
                        {companyLinks.map((link) => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center space-x-3 py-2.5 text-gray-600 hover:text-primary-blue font-medium whitespace-nowrap"
                          >
                            <i className={`${link.icon} text-primary-green`}></i>
                            <span>{link.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Legal & Support Menu - Accordion */}
              <div className="border-t border-gray-100">
                <button
                  onClick={() => setIsMobileLegalOpen(!isMobileLegalOpen)}
                  className="w-full flex items-center justify-between py-3 text-gray-700 font-medium cursor-pointer"
                >
                  <span>Legal & Support</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileLegalOpen ? 'rotate-180' : ''}`}></i>
                </button>
                <AnimatePresence>
                  {isMobileLegalOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pb-2 space-y-1">
                        {legalLinks.map((link) => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center space-x-3 py-2.5 text-gray-600 hover:text-primary-blue font-medium whitespace-nowrap"
                          >
                            <i className={`${link.icon} text-primary-blue`}></i>
                            <span>{link.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/tracking"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-gray-700 hover:text-primary-blue font-medium whitespace-nowrap border-t border-gray-100"
              >
                Tracking
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-gray-700 hover:text-primary-blue font-medium whitespace-nowrap"
              >
                Contact
              </Link>
              
              {/* Mobile Language Switcher */}
              <div className="py-3 border-t border-gray-100">
                <p className="text-sm text-gray-500 mb-3">Language</p>
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code, lang.label)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                        currentLang === lang.label
                          ? 'bg-primary-blue text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="font-medium text-sm">{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Link
                to="/tracking"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-center px-6 py-3 bg-primary-green text-white font-semibold rounded-lg whitespace-nowrap mt-4"
              >
                Track Package
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
